import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["alerta"]

  connect() {
    this.element.querySelectorAll(".form-control.border-danger, .form-select.border-danger").forEach(el => {
      el.classList.remove("border-danger")
      el.dataset.validationRequired = "true"
    })

    // Validate plain inputs immediately; re-run after TomSelect/flatpickr connect
    this._revalidarTodos()
    setTimeout(() => this._revalidarTodos(), 200)

    const form = this.element.tagName === "FORM" ? this.element : this.element.closest("form")
    if (form) {
      form.addEventListener("change", this._onFieldChange.bind(this))
      form.addEventListener("focusout", this._onFieldChange.bind(this))

      form.addEventListener("submit", (e) => this._validarFormulario(e, form), { capture: true })
      form.addEventListener("keydown", (e) => this._interceptarEnter(e, form))

      // Custom event to force revalidation (e.g. from other controllers)
      form.addEventListener("form-validation:revalidate", () => this._revalidarTodos())
    }
  }

  _onFieldChange(e) {
    const el = e.target
    if (el && (el.classList.contains("form-control") || el.classList.contains("form-select"))) {
      this._validarCampo(el)
    }
  }

  _interceptarEnter(e, form) {
    if (e.key !== "Enter") return

    const el = e.target
    const tag = el.tagName.toLowerCase()
    const type = (el.type || "").toLowerCase()

    if (tag === "textarea" || tag === "button") return
    if (type === "submit" || type === "button") return

    const fields = this._camposFocaveis(form)

    // TomSelect: o evento vem do input interno — mapear para o .ts-wrapper na lista
    const chave = el.closest(".ts-wrapper") || el
    const idx = fields.indexOf(chave)

    // Elemento não mapeado → não interferir (evita travar)
    if (idx === -1) return

    e.preventDefault()

    if (idx < fields.length - 1) {
      const proximo = fields[idx + 1]

      if (proximo.classList.contains("ts-wrapper")) {
        // Focar input visível do TomSelect
        const tsInput = proximo.querySelector(".ts-control input")
        tsInput ? tsInput.focus() : proximo.focus()
      } else {
        proximo.focus()
      }
    } else {
      // Último campo → submeter (passa pela validação)
      form.requestSubmit ? form.requestSubmit() : form.submit()
    }
  }

  _camposFocaveis(form) {
    return Array.from(form.querySelectorAll(
      'input:not([type="hidden"]):not([disabled]), select:not([disabled]), .ts-wrapper:not(.disabled), textarea:not([disabled])'
    )).filter(el => {
      // Excluir selects originais escondidos pelo TomSelect (wrapper é irmão, não pai)
      if (el.tagName === "SELECT" && el.classList.contains("tomselected")) return false
      // Excluir elementos invisíveis
      return el.offsetParent !== null
    })
  }

  _revalidarTodos() {
    this.element.querySelectorAll(".form-control, .form-select").forEach(el => {
      this._validarCampo(el)
    })
  }

  _validarCampo(el) {
    if (!el || typeof el.value === 'undefined') return

    // flatpickr altInput: skip, handled by propagation from original input
    if (el.classList.contains("flatpickr-input") && !el._flatpickr) return
    if (el.classList.contains("ts-wrapper")) return

    const val = String(el.value || "")
    const vazio = val.trim() === "" || val === el.getAttribute("placeholder")
    const obrigatorio = el.required || el.dataset.validationRequired === "true"
    const invalido = el.validity && !el.validity.valid

    if (invalido || (obrigatorio && vazio)) {
      this._setEstado(el, "invalid")
    } else if (obrigatorio && !vazio) {
      this._setEstado(el, "valid")
    } else if (!vazio) {
      this._setEstado(el, "filled")
    } else {
      this._setEstado(el, null)
    }
  }

  _setEstado(el, estado) {
    el.classList.remove("border-danger")
    el.classList.toggle("is-valid", estado === "valid")
    el.classList.toggle("is-invalid", estado === "invalid")
    el.classList.toggle("is-filled", estado === "valid" || estado === "filled")

    const altInput = el._flatpickr?.altInput
    if (altInput) {
      altInput.classList.remove("border-danger")
      altInput.classList.toggle("is-valid", estado === "valid")
      altInput.classList.toggle("is-invalid", estado === "invalid")
      altInput.classList.toggle("is-filled", estado === "valid" || estado === "filled")
    }

    const tsWrapper = this._tsWrapper(el)
    if (tsWrapper) {
      tsWrapper.classList.remove("border-danger")
      tsWrapper.classList.toggle("is-valid", estado === "valid")
      tsWrapper.classList.toggle("is-invalid", estado === "invalid")
      tsWrapper.classList.toggle("is-filled", estado === "valid" || estado === "filled")
    }
  }

  _tsWrapper(el) {
    if (el.tomselect && el.tomselect.wrapper) return el.tomselect.wrapper
    const sib = el.nextElementSibling
    return sib && sib.classList.contains("ts-wrapper") ? sib : null
  }

  _validarFormulario(e, form) {
    let valido = true
    form.querySelectorAll("[required], [data-validation-required='true']").forEach(el => {
      this._validarCampo(el)
      if (el.classList.contains("is-invalid")) valido = false
    })
    if (!valido) {
      e.preventDefault()
      e.stopImmediatePropagation()
      const primeiro = form.querySelector(".is-invalid")
      if (primeiro) primeiro.scrollIntoView({ behavior: "smooth", block: "center" })
      this._alerta("Preencha os campos obrigatórios destacados.", "danger")
    }
  }

  _alerta(msg, tipo = "warning", isHtml = false) {
    let el = this.hasAlertaTarget ? this.alertaTarget : this._criarAlerta()
    el.className = `alert alert-${tipo} alert-dismissible position-fixed top-0 start-50 translate-middle-x mt-3 shadow`
    el.style.cssText = "z-index:9999;display:block;max-width:520px;"

    const fecharBtn = `<button type="button" class="btn-close ms-2" aria-label="Fechar"></button>`
    if (isHtml) {
      el.innerHTML = msg + fecharBtn
    } else {
      el.textContent = msg
      el.insertAdjacentHTML("beforeend", fecharBtn)
    }

    el.querySelector(".btn-close")?.addEventListener("click", () => {
      el.style.display = "none"
    }, { once: true })

    clearTimeout(this._alertaTimer)
    if (!isHtml) {
      this._alertaTimer = setTimeout(() => { el.style.display = "none" }, 5000)
    }
  }

  _criarAlerta() {
    const el = document.createElement("div")
    el.setAttribute("data-form-validation-target", "alerta")
    document.body.appendChild(el)
    return el
  }
}
